import { BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

export interface VocabTerm {
  id: string;
  term: string;
  definition: string;
  example?: string | null;
}

interface Props {
  terms: VocabTerm[];
}

export default function VocabularySection({ terms }: Props) {
  const [expanded, setExpanded] = useState(false);

  if (!terms.length) return null;

  const visible = expanded ? terms : terms.slice(0, 3);

  return (
    <section className="relative space-y-3 rounded-2xl border border-[hsl(265_70%_70%/0.22)] bg-gradient-to-br from-[hsl(265_50%_18%/0.5)] via-card/40 to-[hsl(280_45%_16%/0.3)] p-4 shadow-[0_0_28px_-14px_hsl(265_80%_60%/0.5)]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-[hsl(265_85%_72%/0.6)] to-transparent" />
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(265_70%_55%/0.2)] ring-1 ring-[hsl(265_85%_72%/0.4)]">
          <BookOpen className="h-3.5 w-3.5 text-[hsl(265_90%_78%)]" />
        </div>
        <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(265_70%_82%)]">
          Key Vocabulary
        </h3>
        <span className="ml-auto rounded-full bg-[hsl(265_60%_45%/0.22)] ring-1 ring-[hsl(265_80%_70%/0.3)] px-2 py-0.5 text-[10px] font-heading font-medium text-[hsl(265_80%_85%)]">
          {terms.length} {terms.length === 1 ? "term" : "terms"}
        </span>
      </div>

      <div className="space-y-2">
        {visible.map((t) => (
          <div
            key={t.id}
            className="rounded-xl border border-[hsl(265_60%_60%/0.18)] bg-background/35 p-4 transition-colors hover:border-[hsl(265_85%_72%/0.4)] hover:bg-[hsl(265_50%_25%/0.25)]"
          >
            <p className="font-heading text-sm font-bold text-[hsl(265_85%_88%)]">{t.term}</p>
            <p className="mt-1 font-body text-sm leading-relaxed text-foreground/80">{t.definition}</p>
            {t.example && (
              <p className="mt-2 font-body text-xs italic leading-relaxed text-muted-foreground">
                Example: {t.example}
              </p>
            )}
          </div>
        ))}
      </div>

      {terms.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 rounded-lg border border-[hsl(265_70%_65%/0.3)] bg-[hsl(265_60%_40%/0.2)] px-3 py-1.5 text-xs font-heading font-medium text-[hsl(265_85%_85%)] hover:bg-[hsl(265_60%_45%/0.3)] hover:border-[hsl(265_85%_72%/0.5)] transition-colors"
        >
          {expanded ? (
            <>Show less <ChevronUp className="h-3.5 w-3.5" /></>
          ) : (
            <>Show all {terms.length} terms <ChevronDown className="h-3.5 w-3.5" /></>
          )}
        </button>
      )}
    </section>
  );
}
